import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useQueryClient} from '@tanstack/react-query'
import {
    Button,
    Input,
    Box,
    Flex,
    Text,
    HStack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useSegment from '../../integrations/segment/app/hooks/use-segment'
import {useCustomObject} from '../../hooks/use-custom-object'
import {HeartIconBlack, HeartIconRed, BellIcon} from '../icons'

const BackInStock = ({
    product,
    isComingSoon,
    onWishlistClick,
    isWishlistLoading,
    canAddToWishlist,
    productStyles,
    isFavourite,
    isMobile
}) => {
    const {formatDate} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const segment = useSegment()
    const toast = useToast()
    const intl = useIntl()
    const queryClient = useQueryClient()
    const initialEmail = customer?.email || ''
    const [email, setEmail] = useState(initialEmail)
    const [inputMessage, setInputMessage] = useState('')
    const [isEmailError, setIsEmailError] = useState(false)
    const [triggerFetch, setTriggerFetch] = useState(false)
    const customObjectQuery = useCustomObject(
        triggerFetch
            ? {
                  method: 'POST',
                  objectTypeId: 'BackInStockNotifications',
                  objectKey: `${product?.id}_${email}`,
                  reqBody: {
                      productID: product?.id,
                      email: email
                  }
              }
            : null
    )

    useEffect(() => {
        if (customer?.email) setEmail(customer?.email)
    }, [customer?.email])

    useEffect(() => {
        if (!customObjectQuery.isFetching && triggerFetch) {
            const status = customObjectQuery?.data?.status

            if (status === 200) {
                toast({
                    title: intl.formatMessage({
                        id: 'pdp_bisn_success_title',
                        defaultMessage: 'Subscribed Successfully'
                    }),
                    description: intl.formatMessage({
                        id: 'pdp_bisn_success_description',
                        defaultMessage: 'We’ll notify you when the item is available for purchase.'
                    }),
                    status: 'success',
                    duration: 4000,
                    isClosable: false,
                    position: 'top-right'
                })
                setIsEmailError(false)
                segment.sendBackInStock('Back In Stock Subscribed', email, product)
            } else if (status === 409) {
                toast({
                    title: intl.formatMessage({
                        id: 'pdp_bisn_already_title',
                        defaultMessage: 'Already Subscribed'
                    }),
                    description: intl.formatMessage({
                        id: 'pdp_bisn_already_description',
                        defaultMessage: 'You’ve already requested to be notified for this item.'
                    }),
                    status: 'info',
                    duration: 4000,
                    isClosable: false,
                    position: 'top-right'
                })
                setIsEmailError(true)
            } else {
                toast({
                    title: intl.formatMessage({
                        id: 'toast_error_fallback',
                        defaultMessage: 'Something went wrong.'
                    }),
                    description: intl.formatMessage({
                        id: 'toast_error_fallback',
                        defaultMessage: 'Something went wrong.'
                    }),
                    status: 'error',
                    duration: 4000,
                    isClosable: false,
                    position: 'top-right'
                })
                setIsEmailError(true)
            }

            setTriggerFetch(false)
        }
    }, [customObjectQuery.isFetching, triggerFetch])

    const handleNotify = () => {
        const enteredEmail = email?.trim()

        if (!enteredEmail) {
            setIsEmailError(true)
            setInputMessage('Please enter an email address.')
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(enteredEmail)) {
            setIsEmailError(true)
            setInputMessage('Please enter a valid email address.')
            return
        }

        queryClient.removeQueries([
            'custom',
            'custom Objects',
            `BackInStockNotifications/${product?.id}_${enteredEmail}`
        ])

        setEmail(enteredEmail)
        setTriggerFetch(true)
    }

    return (
        <>
            <Box>
                <Box data-testid="product-unavailable">
                    <Box>
                        {!isComingSoon ? (
                            <>
                                <Text
                                    mb={2}
                                    fontWeight={'extrabold'}
                                    fontSize="xl"
                                    lineHeight={'28px'}
                                >
                                    <FormattedMessage
                                        id="pdp_bsin_currently_unavailable"
                                        defaultMessage="Currently Unavailable"
                                    />
                                </Text>
                                <Text fontSize={'md'} lineHeight={'24px'}>
                                    <FormattedMessage
                                        defaultMessage="Get notified when this product is available in the future."
                                        id="pdp_bsin_outofstock"
                                    />
                                </Text>
                            </>
                        ) : (
                            <>
                                <Text fontWeight={'extrabold'} fontSize="xl" lineHeight={'28px'}>
                                    <FormattedMessage
                                        id="pdp_bisn_coming_soon"
                                        defaultMessage="Coming soon"
                                    />
                                </Text>
                                <Text mb={2}>
                                    <FormattedMessage
                                        id="pdp_bisn_expected_release_date"
                                        defaultMessage="Get notified when this product is available. Expected release date is {releaseDate}"
                                        values={{
                                            releaseDate: (
                                                <Box fontWeight={'bold'} display={'inline-block'}>
                                                    {formatDate(new Date(product?.c_streetDate), {
                                                        year: 'numeric',
                                                        day: 'numeric',
                                                        month: 'long'
                                                    })}
                                                </Box>
                                            )
                                        }}
                                    />
                                </Text>
                            </>
                        )}

                        <Flex mt={4} mb={3} gap="8px" direction={{base: 'column', sm: 'row'}}>
                            <HStack
                                spacing={0}
                                borderRadius="md"
                                overflow="hidden"
                                boxShadow="sm"
                                w="100%"
                                borderRight="0"
                            >
                                <Input
                                    placeholder={intl.formatMessage({
                                        id: 'pdp_bisn_email_placeholder',
                                        defaultMessage: 'Your Email Address'
                                    })}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value)
                                        if (isEmailError) {
                                            setIsEmailError(false)
                                            setInputMessage('')
                                        }
                                    }}
                                    borderRadius="lg"
                                    flex="0.8"
                                    borderRightRadius="0"
                                    bgColor={'white'}
                                    fontWeight={'medium'}
                                    p={'4'}
                                />
                                <Button
                                    key="notify-button"
                                    variant="secondary"
                                    onClick={handleNotify}
                                    flex={{base: '0.4', lg: '0.2'}}
                                    height="12"
                                    borderTopRightRadius="8px"
                                    borderBottomRightRadius="8px"
                                    borderTopLeftRadius="0"
                                    borderBottomLeftRadius="0"
                                    lineHeight="6"
                                    fontWeight="extrabold"
                                >
                                    <BellIcon width={'20px'} height={'20px'} marginRight={'1'} />
                                    <FormattedMessage
                                        id="pdp_bsin_notifiy_btn"
                                        defaultMessage="Notify Me"
                                    />
                                </Button>
                            </HStack>
                            <Box
                                className="notify-me-wishlist"
                                width={{base: '100%', sm: '48px'}}
                                flexShrink="0"
                            >
                                <Button
                                    key="wishlist-button"
                                    variant="outline"
                                    className="wishlist-button"
                                    onClick={onWishlistClick}
                                    disabled={isWishlistLoading || !canAddToWishlist}
                                    isLoading={isWishlistLoading}
                                    aria-label={'wishlist button'}
                                    {...productStyles.wishlistButton}
                                >
                                    {isMobile ? (
                                        isFavourite ? (
                                            'Remove from wishlist'
                                        ) : (
                                            'Add to wishlist'
                                        )
                                    ) : isFavourite ? (
                                        <HeartIconRed />
                                    ) : (
                                        <HeartIconBlack />
                                    )}
                                </Button>
                            </Box>
                        </Flex>

                        {isEmailError && (
                            <Text color={'#f47521'} fontWeight={'700'}>
                                {inputMessage}
                            </Text>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    )
}

BackInStock.propTypes = {
    product: PropTypes.shape({
        product: PropTypes.shape({
            id: PropTypes.string.isRequired
        }).isRequired,
        c_streetDate: PropTypes.string,
        id: PropTypes.string.isRequired
    }).isRequired,
    isComingSoon: PropTypes.bool,
    onWishlistClick: PropTypes.func.isRequired,
    isWishlistLoading: PropTypes.bool.isRequired,
    canAddToWishlist: PropTypes.bool.isRequired,
    productStyles: PropTypes.object,
    isFavourite: PropTypes.bool,
    isMobile: PropTypes.bool.isRequired
}

export default BackInStock
